import React, { useCallback, useEffect, useState } from "react";
import { Modal, Box, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import styled from "styled-components";

import RequestV2 from "../../../api/RequestV2";
import Config from "../../../utils/config";
import { getApiEnvelopeMessage } from "../../core/apiErrorMessage";

import {
  PageBodyTitle,
  Card,
  Button,
  Table,
  PageLoader,
  SuccessMessage,
  ErrorMessage,
  StatusBadge,
  InputField,
} from "../../design-system";

import { theme } from "../../design-system/theme";
import { CsvDown } from "../../integrations/export";

const PageWrapper = styled.div`
  max-width: 1440px;
  width: 100%;
  margin: 0 auto;
  padding: ${theme.spacing.sm} 0 0;
`;

const TopBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.lg};
  margin-bottom: ${theme.spacing.lg};
`;

const ActionsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

const maintenanceSchema = Yup.object().shape({
  startTime: Yup.string().required("Start time is required"),

  endTime: Yup.string()
    .required("End time is required")
    .test(
      "end-after-start",
      "End time must be after start time",
      function (value) {
        const { startTime } = this.parent;

        if (!startTime || !value) {
          return true;
        }

        return new Date(value) > new Date(startTime);
      }
    ),

  servicesDown: Yup.array()
    .min(1, "Select at least one service")
    .required("Service is required"),

  reason: Yup.string()
    .trim()
    .required("Reason is required"),
});

const SERVICE_OPTIONS = [
  {
    label: "User Service",
    value: "USER_SERVICE",
  },
  {
    label: "Payment Service",
    value: "PAYMENT_SERVICE",
  },
  {
    label: "Reporting Service",
    value: "REPORTING_SERVICE",
  },
  {
    label: "API Service",
    value: "API_SERVICE",
  },
];
const MAINTENANCE_DOWNLOAD_PAYLOAD = {
  pagination: {
    pageNo: 1,
    pageSize: 500,
  },
};
const MAINTENANCE_CSV_HEADERS = [
  { label: "S.No", key: "_sno" },
  { label: "Start Time", key: "startTime" },
  { label: "End Time", key: "endTime" },
  { label: "Services Down", key: "servicesDown" },
  { label: "Reason", key: "reason" },
  { label: "Status", key: "status" },
];

const getInitialValues = () => ({
  startTime: "",
  endTime: "",
  servicesDown: [],
  reason: "",
});

const formatDateTime = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Converts API datetime into the format required by
 * <input type="datetime-local" />
 *
 * Example:
 * 2026-09-23T17:00:00
 * ->
 * 2026-09-23T17:00
 */
const formatDateTimeLocal = (value) => {
  if (!value) return "";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value).slice(0, 16);
  }

  const pad = (num) => String(num).padStart(2, "0");

  return `${date.getFullYear()}-${pad(
    date.getMonth() + 1
  )}-${pad(date.getDate())}T${pad(
    date.getHours()
  )}:${pad(date.getMinutes())}`;
};

export default function ScheduleMaintenance() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [dataSet, setDataSet] = useState("");

  const [editingRow, setEditingRow] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);  

  const isEditMode = Boolean(editingRow);

  const handleOpenCreate = () => {
    setMessage("");
    setErrorMsg("");
    setEditingRow(null);
    setOpen(true);
  };

  const handleOpenUpdate = (row) => {
    setMessage("");
    setErrorMsg("");
    setEditingRow(row);
    setOpen(true);
  };

  const handleClose = () => {
    if (!submitting) {
      setOpen(false);
      setEditingRow(null);
    }
  };

  /**
   * GET ALL MAINTENANCE
   */
  const parseServicesDown = (value) => {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);

      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error(
        "Failed to parse servicesDown:",
        value,
        error
      );

      return [];
    }
  }

  return [];
};

const transformMaintenanceRows = (rows) =>
  rows.map((row, index) => ({
    _sno: index + 1,
    startTime: formatDateTime(row?.startTime),
    endTime: formatDateTime(row?.endTime),
    servicesDown: parseServicesDown(row?.servicesDown).join(", "),
    reason: row?.reason || "—",
    status: row?.status || "—",
  }));
  const fetchList = useCallback((pageNo = 1, size = 10) => {
  setLoading(true);
  setErrorMsg("");

  const api = new RequestV2(
    "",
    (data) => {
      setLoading(false);

      console.log("Maintenance list response:", data);

      const root = data?.data ?? data;

      const content = Array.isArray(root)
        ? root
        : root?.content ?? root?.data ?? [];

      const list = Array.isArray(content) ? content : [];

      const formattedRows = list.map((row, idx) => ({
        ...row,
        servicesDown: parseServicesDown(row?.servicesDown),
        // S.No across pages: (pageNo - 1) * size + idx + 1
        _sno: (pageNo - 1) * size + idx + 1,
      }));

      setRows(formattedRows);

      // Prefer server pagination meta when present
      setTotalPages(root?.totalPages ?? data?.totalPages ?? 1);
      setTotalElements(
        root?.totalElements ??
          data?.totalElements ??
          (Array.isArray(root) ? root.length : list.length) ??
          0
      );
    },
    (err) => {
      setLoading(false);
      setRows([]);
      setTotalPages(1);
      setTotalElements(0);

      const errorMessage = getApiEnvelopeMessage(
        err?.response?.data,
        "Failed to load maintenance schedules."
      );

      setErrorMsg(errorMessage);
      setDataSet(String(Date.now()));
    }
  );

  api.post(`${Config.apis.user.MAINTENANCE_GET_ALL}`, {
    pagination: {
      pageNo,
      pageSize: size,
    },
  });
}, []);

    useEffect(() => {
        fetchList(currentPage, pageSize);
    }, [currentPage, pageSize, fetchList]);

  /**
   * CREATE MAINTENANCE
   */
  const handleCreate = (values, { resetForm }) => {
    setSubmitting(true);
    setMessage("");
    setErrorMsg("");

    const payload = {
      startTime: values.startTime,
      endTime: values.endTime,
      servicesDown: values.servicesDown,
      reason: values.reason.trim(),
    };

    console.log("Create maintenance payload:", payload);

    const api = new RequestV2(
      "",
      (data) => {
        setSubmitting(false);

        console.log("Create maintenance response:", data);

        const response = data?.data ?? data;

        if (response?.code === "0000") {
          setMessage("Maintenance scheduled successfully.");
          setDataSet(String(Date.now()));

          resetForm();
          setOpen(false);
          setEditingRow(null);

          fetchList();
        } else {
          setErrorMsg(
            getApiEnvelopeMessage(
              data?.data ?? data,
              "Failed to schedule maintenance."
            )
          );

          setDataSet(String(Date.now()));
        }
      },
      (err) => {
        setSubmitting(false);

        const errorMessage = getApiEnvelopeMessage(
          err?.response?.data,
          "Failed to schedule maintenance."
        );

        setErrorMsg(errorMessage);
        setDataSet(String(Date.now()));
      }
    );

    api.post(
      `${Config.apis.user.MAINTENANCE_CREATE}`,
      payload
    );
  };
  const handleUpdate = (values) => {
    if (!editingRow?.uuid) {
      setErrorMsg("Maintenance UUID is missing.");
      setDataSet(String(Date.now()));
      return;
    }

    setSubmitting(true);
    setMessage("");
    setErrorMsg("");

    const payload = {
      uuid: editingRow.uuid,
      startTime: values.startTime,
      endTime: values.endTime,
      servicesDown: values.servicesDown,
      reason: values.reason.trim(),
    };

    console.log("Update maintenance payload:", payload);

    const api = new RequestV2(
      "",
      (data) => {
        setSubmitting(false);

        console.log("Update maintenance response:", data);

        const response = data?.data ?? data;

        if (response?.code === "0000") {
          setMessage("Maintenance updated successfully.");
          setDataSet(String(Date.now()));

          setOpen(false);
          setEditingRow(null);

          fetchList();
        } else {
          setErrorMsg(
            getApiEnvelopeMessage(
              data?.data ?? data,
              "Failed to update maintenance."
            )
          );

          setDataSet(String(Date.now()));
        }
      },
      (err) => {
        setSubmitting(false);

        const errorMessage = getApiEnvelopeMessage(
          err?.response?.data,
          "Failed to update maintenance."
        );

        setErrorMsg(errorMessage);
        setDataSet(String(Date.now()));
      }
    );

    api.put(
      `${Config.apis.user.MAINTENANCE_UPDATE}`,
      payload
    );
  };

  const handleSubmit = (values, formikHelpers) => {
    if (isEditMode) {
      handleUpdate(values);
      return;
    }

    handleCreate(values, formikHelpers);
  };

  const columns = [
    {
      key: "_sno",
      label: "S.No",
      align: "center",
      headerAlign: "center",
      cellAlign: "center",
      width: 56,
      render: (v) => v ?? "—",
    },

    {
      key: "startTime",
      label: "Start time",
      align: "left",
      headerAlign: "left",
      cellAlign: "left",
      render: (_, row) =>
        formatDateTime(row?.startTime),
    },

    {
      key: "endTime",
      label: "End time",
      align: "left",
      headerAlign: "left",
      cellAlign: "left",
      render: (_, row) =>
        formatDateTime(row?.endTime),
    },

    {
      key: "servicesDown",
      label: "Services down",
      align: "left",
      headerAlign: "left",
      cellAlign: "left",
      wrap: true,
      render: (_, row) =>
        Array.isArray(row?.servicesDown)
          ? row.servicesDown.join(", ")
          : row?.servicesDown || "—",
    },

    {
      key: "reason",
      label: "Reason",
      align: "left",
      headerAlign: "left",
      cellAlign: "left",
      wrap: true,
      render: (_, row) => row?.reason || "—",
    },

    {
      key: "status",
      label: "Status",
      align: "center",
      headerAlign: "center",
      cellAlign: "center",
      render: (_, row) =>
        row?.status ? (
          <StatusBadge status={row.status} />
        ) : (
          "—"
        ),
    },

    {
        key: "action",
        label: "Action",
        align: "center",
        headerAlign: "center",
        cellAlign: "center",
        render: (_, row) => (
            <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={String(row?.status).toUpperCase() === "COMPLETED"}
                onClick={() => handleOpenUpdate(row)}
            >
                Update
            </Button>
        ),
    },
  ];

  /**
   * Initial values for create/update form
   */
  const formInitialValues = isEditMode
    ? {
        startTime: formatDateTimeLocal(
          editingRow?.startTime
        ),
        endTime: formatDateTimeLocal(
          editingRow?.endTime
        ),
        servicesDown: Array.isArray(
          editingRow?.servicesDown
        )
          ? editingRow.servicesDown
          : [],
        reason: editingRow?.reason || "",
      }
    : getInitialValues();

  if (loading && rows.length === 0) {
    return <PageLoader />;
  }

  return (
    <PageWrapper>
          <TopBar>
              <PageBodyTitle>
                  Schedule maintenance
              </PageBodyTitle>

              <ActionsRow>
                  <CsvDown
                      headers={MAINTENANCE_CSV_HEADERS}
                      params={{
                          pagination: {
                              pageNo: 1,
                              pageSize: totalElements || 500,
                          },
                      }}
                      url={Config.apis.user.MAINTENANCE_GET_ALL}
                      reportName="maintenance-schedule.csv"
                      transformRows={transformMaintenanceRows}
                      useV2
                  />

                  <Button
                      type="button"
                      variant="primary"
                      size="md"
                      onClick={handleOpenCreate}
                  >
                      Schedule maintenance
                  </Button>
              </ActionsRow>
          </TopBar>

      {message && (
        <SuccessMessage
          successMessage={message}
          dataset={dataSet}
        />
      )}

      {errorMsg && (
        <ErrorMessage
          error={errorMsg}
          datetimestamp={dataSet}
        />
      )}

      <Card style={{ marginTop: theme.spacing.md }}>
        <Table
         data={rows}
         columns={columns}
         rowKey={(row) =>
           row?.uuid || row?.id || row?.maintenanceId || row?._sno
         }
         loading={loading}
         emptyMessage="No maintenance schedule found"
         pagination={{
           currentPage,
           pageSize,
           total: totalElements || totalPages * pageSize,
           onPageChange: setCurrentPage,
           pageSizeOptions: [10, 20, 50],
           onPageSizeChange: (s) => {
             setPageSize(s);
             setCurrentPage(1);
           },
         }}
       />
      </Card>

      <Modal
        open={open}
        onClose={handleClose}
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            maxWidth: "95vw",
            maxHeight: "90vh",
            overflow: "hidden",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 3,
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="h6"
            component="h2"
            textAlign="center"
            mb={2}
          >
            {isEditMode
              ? "Update Maintenance"
              : "Schedule Maintenance"}
          </Typography>

          <Formik
            enableReinitialize
            initialValues={formInitialValues}
            validationSchema={maintenanceSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              setFieldValue,
              setFieldTouched,
            }) => (
              <Form
                style={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    maxHeight: "65vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                    pr: 0.5,
                    scrollbarGutter: "stable",
                  }}
                >
                  {/* START TIME */}
                  <div
                    style={{
                      marginBottom: theme.spacing.sm,
                      paddingTop: "6px",
                    }}
                  >
                    <InputField
                      type="datetime-local"
                      label="Start time"
                      name="startTime"
                      value={values.startTime}
                      onChange={(value) =>
                        setFieldValue(
                          "startTime",
                          value
                        )
                      }
                      onBlur={() =>
                        setFieldTouched(
                          "startTime",
                          true
                        )
                      }
                      error={
                        touched.startTime &&
                        errors.startTime
                          ? String(
                              errors.startTime
                            )
                          : undefined
                      }
                    />
                  </div>

                  {/* END TIME */}
                  <div
                    style={{
                      marginBottom: theme.spacing.sm,
                    }}
                  >
                    <InputField
                      type="datetime-local"
                      label="End time"
                      name="endTime"
                      value={values.endTime}
                      onChange={(value) =>
                        setFieldValue(
                          "endTime",
                          value
                        )
                      }
                      onBlur={() =>
                        setFieldTouched(
                          "endTime",
                          true
                        )
                      }
                      error={
                        touched.endTime &&
                        errors.endTime
                          ? String(
                              errors.endTime
                            )
                          : undefined
                      }
                    />
                  </div>

                  {/* SERVICES DOWN */}
                  <div
                    style={{
                      marginBottom: theme.spacing.sm,
                    }}
                  >
                    <InputField
                      type="select"
                      name="servicesDown"
                      label="Services down"
                      value={
                        Array.isArray(
                          values.servicesDown
                        )
                          ? values.servicesDown
                          : []
                      }
                      options={SERVICE_OPTIONS}
                      multiple
                      onChange={(value) => {
                        const services =
                          Array.isArray(value)
                            ? value
                            : value
                            ? [value]
                            : [];

                        setFieldValue(
                          "servicesDown",
                          services
                        );
                      }}
                      onBlur={() =>
                        setFieldTouched(
                          "servicesDown",
                          true
                        )
                      }
                      error={
                        touched.servicesDown &&
                        errors.servicesDown
                          ? String(
                              errors.servicesDown
                            )
                          : undefined
                      }
                      placeholder="Select services"
                    />
                  </div>

                  {/* REASON */}
                  <div
                    style={{
                      marginBottom: theme.spacing.sm,
                    }}
                  >
                    <InputField
                      type="text"
                      name="reason"
                      label="Reason"
                      value={values.reason}
                      onChange={(value) =>
                        setFieldValue(
                          "reason",
                          value
                        )
                      }
                      onBlur={() =>
                        setFieldTouched(
                          "reason",
                          true
                        )
                      }
                      error={
                        touched.reason &&
                        errors.reason
                          ? String(
                              errors.reason
                            )
                          : undefined
                      }
                      placeholder="Enter reason"
                    />
                  </div>
                </Box>

                {/* FOOTER */}
                <Box
                  mt={3}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="md"
                    disabled={submitting}
                    onClick={handleClose}
                  >
                    Cancel
                  </Button>

                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    disabled={submitting}
                  >
                    {submitting
                      ? isEditMode
                        ? "Updating..."
                        : "Scheduling..."
                      : isEditMode
                      ? "Update"
                      : "Schedule"}
                  </Button>
                </Box>
              </Form>
            )}
          </Formik>
        </Box>
      </Modal>
    </PageWrapper>
  );
}